import { bootstrapApplication } from '@angular/platform-browser';
import { PreloadAllModules, provideRouter, RouteReuseStrategy, withPreloading } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';

import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeUk from '@angular/common/locales/uk';
import { importProvidersFrom, inject, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StorageService } from '@core/services/storage.service';
import { IonicModule } from '@ionic/angular';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { reducers } from '@store/index';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
// import { AppEffects } from '@store/app/app.effect';
import { initApp } from '@core/init-app-fn';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import { AppCapacitorService } from '@core/services/app-capacitor.service';
import { ColorSchemaService } from '@core/services/color-schema.service';
import { TextZoomService } from '@core/services/text-zoom.service';
import { TranslateCommonService } from '@core/services/translate.service';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideEnvironmentNgxMask } from 'ngx-mask';
// import { StoreSyncKeysEnum } from '@core/enums/store-keys.enum';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatPaginatorIntl } from '@angular/material/paginator';

registerLocaleData(localeUk);

export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const maskConfig: Partial<any> = {
    validation: false,
};

export function paginatorIntlFactory(translate: TranslateService) {
    const intl = new MatPaginatorIntl();

    const setLabels = () => {
        intl.itemsPerPageLabel = translate.instant('paginator.items');
        intl.nextPageLabel = translate.instant('paginator.next');
        intl.previousPageLabel = translate.instant('paginator.prev');
        intl.firstPageLabel = translate.instant('paginator.first');
        intl.lastPageLabel = translate.instant('paginator.last');

        intl.getRangeLabel = (page, pageSize, length) => {
            if (length === 0 || pageSize === 0) return translate.instant('paginator.empty');
            const start = page * pageSize + 1;
            const end = Math.min((page + 1) * pageSize, length);

            return translate.instant('paginator.range', { start, end, length });
        };
        intl.changes.next();
    };

    setLabels();
    translate.onLangChange.subscribe(() => setLabels());

    return intl;
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            BrowserAnimationsModule,
            IonicModule.forRoot({ innerHTMLTemplatesEnabled: true }),
            IonicStorageModule.forRoot({
                name: '_ncodpdb',
                driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
            }),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpClient],
                },
                defaultLanguage: 'uk',
            }),
            StoreModule.forRoot(reducers, { metaReducers: [] }),
            StoreDevtoolsModule.instrument({
                maxAge: 25,
                logOnly: true,
            }),
        ),
        { provide: LOCALE_ID, useValue: 'uk-UK' },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: MatPaginatorIntl, useFactory: paginatorIntlFactory, deps: [TranslateService] },
        provideAppInitializer(() => {
            return initApp(
                inject(TranslateCommonService),
                inject(StorageService),
                inject(ColorSchemaService),
                inject(Store),
                inject(TextZoomService),
            );
        }),
        provideIonicAngular({
            animated: false,
        }),
        provideRouter(routes, withPreloading(PreloadAllModules)),
        provideHttpClient(withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        provideEnvironmentNgxMask(maskConfig),
        DatePipe,
        AppCapacitorService,
        importProvidersFrom(ScrollingModule),
    ],
}).then();
